import viewService from "@/services/viewService";
import EntityService from "@/services/entityService";
import relatorioContasAReceberService from "@/views/Relatorios/services/relatorioContasAReceberService";
import relatorioNFEmitidaService from "@/views/Relatorios/services/relatorioNFEmitidaService";

const projectService = new EntityService("Project");
const collaboratorService = new EntityService("Collaborator");

const JOB_VIEW_ID = "d6b789aa-34e6-4403-a5b8-7ee19c20efb8";
const CONTAS_A_RECEBER_VIEW_NAME = "contas-a-receber";

const toNumber = (valor) => {
  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : 0;
};

const toDateKey = (valor) => {
  if (!valor) return null;

  if (typeof valor === "string") {
    const valorLimpo = valor.trim();
    const matchIsoComHora = valorLimpo.match(/^(\d{4})-(\d{2})-(\d{2})T/);
    if (matchIsoComHora) {
      const [, ano, mes, dia] = matchIsoComHora;
      return Date.UTC(Number(ano), Number(mes) - 1, Number(dia));
    }

    const matchIso = valorLimpo.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (matchIso) {
      const [, ano, mes, dia] = matchIso;
      return Date.UTC(Number(ano), Number(mes) - 1, Number(dia));
    }

    const matchBr = valorLimpo.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (matchBr) {
      const [, dia, mes, ano] = matchBr;
      return Date.UTC(Number(ano), Number(mes) - 1, Number(dia));
    }
  }

  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return null;
  return Date.UTC(data.getFullYear(), data.getMonth(), data.getDate());
};

const isPossivelId = (valor) => {
  if (!valor) return false;
  const texto = String(valor).trim();
  return /^[0-9a-f]{24}$/i.test(texto) || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(texto);
};

const normalizarTexto = (valor) => String(valor || "").trim();
const normalizarCodigoJob = (valor) => normalizarTexto(valor).replace(/\s+/g, "").toUpperCase();
const normalizarComparacao = (valor) => normalizarTexto(valor)
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toUpperCase();

const ehStatusEmitida = (status = "") => {
  const texto = normalizarComparacao(status);
  return texto.includes("EMITIDA");
};

const ehTipoNotaPermitidoNoFaturado = (tipo = "") => {
  const texto = normalizarComparacao(tipo);

  if (!texto) return false;

  const ehVenda = texto.includes("VENDA");
  const ehServico = texto.includes("SERVICO");
  const ehRemessa = texto.includes("REMESSA");
  const ehDevolucao = texto.includes("DEVOLUCAO") || texto.includes("DEVOLUCAO");

  if (ehRemessa || ehDevolucao) return false;
  if (ehVenda || ehServico) return true;

  return false;
};
const formatarDataFimInclusiva = (valor = "") => {
  const texto = normalizarTexto(valor);
  if (!texto) return "";
  return /^\d{4}-\d{2}-\d{2}$/.test(texto) ? `${texto}T23:59:59.999` : texto;
};

const extrairTexto = (valor) => {
  if (Array.isArray(valor)) {
    if (!valor.length) return "";
    return extrairTexto(valor[0]);
  }

  if (valor && typeof valor === "object") {
    return normalizarTexto(valor.name || valor.nome || valor.fullName || valor.displayName || valor.code || valor.codigo);
  }

  return normalizarTexto(valor);
};

const extrairTextoUtil = (...candidatos) => {
  for (const candidato of candidatos) {
    const texto = extrairTexto(candidato);
    if (texto && texto !== "-" && texto.toLowerCase() !== "null" && texto.toLowerCase() !== "undefined") {
      return texto;
    }
  }
  return "";
};

const extrairSiglaNome = (nome = "") => {
  const partes = normalizarTexto(nome)
    .split(/\s+/)
    .filter(Boolean);

  if (!partes.length) return "";
  return partes.map((p) => p.charAt(0).toUpperCase()).join("");
};

const montarSiglaGestorCoordenador = (gestorNome = "", coordenadorNome = "") => {
  const siglaGestor = extrairSiglaNome(gestorNome);
  const siglaCoordenador = extrairSiglaNome(coordenadorNome);

  if (siglaGestor && siglaCoordenador) return `${siglaGestor}/${siglaCoordenador}`;
  if (siglaGestor) return `${siglaGestor}/-`;
  if (siglaCoordenador) return `-/${siglaCoordenador}`;
  return "";
};

const extrairSiglaContratosProjetos = (linha = {}, revisao = {}) => {
  const gestor = extrairTexto(
    revisao?.gestorDeContratosNome ||
    revisao?.gestorDeContratos ||
    linha?.gestorDeContratosNome ||
    linha?.gestorDeContratos ||
    linha?.contractManager ||
    linha?.contractManagerName
  );

  const coordenador = extrairTexto(
    revisao?.coordenadorDeContratosNome ||
    revisao?.coordenadorDeContrato ||
    revisao?.coordenadorContrato ||
    revisao?.coordenador ||
    linha?.coordenadorDeContratosNome ||
    linha?.coordenadorDeContrato ||
    linha?.coordenadorContrato ||
    linha?.coordenador ||
    linha?.contractCoordinator ||
    linha?.contractCoordinatorName
  );

  return montarSiglaGestorCoordenador(gestor, coordenador);
};

const extrairLista = (response) => {
  const lista = response?.data?.data?.data;
  if (Array.isArray(lista)) return lista;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const possuiDadosValidos = (response) => {
  if (!response) return false;
  if (response?.success === false) return false;
  if (response?.data?.success === false) return false;
  return Array.isArray(response?.data?.data?.data) || Array.isArray(response?.data?.data) || Array.isArray(response?.data);
};

// Coleta IDs de coordenador de um registro bruto (linha + revisão).
// Não inclui campos de vendedor/comercial/gestor.
const extrairIdsCoordenador = (linha = {}, revisao = {}) => {
  const candidatos = [
    // Campo principal de contratos
    linha?.coordenadorDeContratos?.id,
    revisao?.coordenadorDeContratos?.id,
    // Campos de projetos
    linha?.coordenadorDeProjetosId,
    revisao?.coordenadorDeProjetosId,
    linha?.coordenadorProjetosId,
    revisao?.coordenadorProjetosId,
    linha?.coordenadorProjetoId,
    revisao?.coordenadorProjetoId,
    // Variações simples de IDs
    linha?.coordenadorId,
    revisao?.coordenadorId,
    // Campo confirmado no Project
    linha?.collaboradorId,
    revisao?.collaboradorId,
    linha?.collaboratorId,
    revisao?.collaboratorId,
    // Campos como string (ID direto)
    typeof linha?.coordenadorDeContratos === "string" ? linha.coordenadorDeContratos : null,
    typeof revisao?.coordenadorDeContratos === "string" ? revisao.coordenadorDeContratos : null,
    typeof linha?.coordenadorDeContrato === "string" ? linha.coordenadorDeContrato : null,
    typeof revisao?.coordenadorDeContrato === "string" ? revisao.coordenadorDeContrato : null,
    typeof linha?.coordenadorContrato === "string" ? linha.coordenadorContrato : null,
    typeof revisao?.coordenadorContrato === "string" ? revisao.coordenadorContrato : null,
    typeof linha?.coordenadorDeProjetosId === "string" ? linha.coordenadorDeProjetosId : null,
    typeof revisao?.coordenadorDeProjetosId === "string" ? revisao.coordenadorDeProjetosId : null,
    // Inglês
    linha?.contractCoordinator,
    revisao?.contractCoordinator,
    linha?.projectCoordinatorId,
    revisao?.projectCoordinatorId,
    // Arrays de coordenadores (pega primeiro)
    Array.isArray(linha?.coordenadoresDeContratos) && linha.coordenadoresDeContratos[0]?.id ? linha.coordenadoresDeContratos[0].id : null,
    Array.isArray(revisao?.coordenadoresDeContratos) && revisao.coordenadoresDeContratos[0]?.id ? revisao.coordenadoresDeContratos[0].id : null,
    Array.isArray(linha?.coordenadores) && linha.coordenadores[0]?.id ? linha.coordenadores[0].id : null,
    Array.isArray(revisao?.coordenadores) && revisao.coordenadores[0]?.id ? revisao.coordenadores[0].id : null
  ];
  const ids = candidatos.filter(isPossivelId);

  return ids;
};

// Resolve o nome do coordenador de contratos.
// Prioridade: texto já no registro → objeto com .nome → lookup no mapa de Collaborator.
// Nunca cai para vendedor, comercial ou gestor.
const resolverNomeCoordenador = (linha = {}, revisao = {}, mapa = {}) => {
  // 1. Campos de texto / objetos com .nome (contratos)
  const textoCandidatos = [
    linha?.coordenadorDeContratos?.nome,
    revisao?.coordenadorDeContratos?.nome,
    linha?.coordenadorDeContratosNome,
    revisao?.coordenadorDeContratosNome,
    typeof linha?.coordenadorDeContratos === "object" && linha.coordenadorDeContratos
      ? extrairTexto(linha.coordenadorDeContratos) : null,
    typeof revisao?.coordenadorDeContratos === "object" && revisao.coordenadorDeContratos
      ? extrairTexto(revisao.coordenadorDeContratos) : null,
    // Campos de projetos
    linha?.coordenadorDeProjetosNome,
    revisao?.coordenadorDeProjetosNome,
    linha?.coordenadorProjetosNome,
    revisao?.coordenadorProjetosNome,
    linha?.coordenadorDeProjetoNome,
    revisao?.coordenadorDeProjetoNome,
    // Variações com "Contrato" singular
    linha?.coordenadorDeContrato?.nome,
    revisao?.coordenadorDeContrato?.nome,
    typeof linha?.coordenadorDeContrato === "object" && linha.coordenadorDeContrato
      ? extrairTexto(linha.coordenadorDeContrato) : null,
    typeof revisao?.coordenadorDeContrato === "object" && revisao.coordenadorDeContrato
      ? extrairTexto(revisao.coordenadorDeContrato) : null,
    linha?.coordenadorContrato?.nome,
    revisao?.coordenadorContrato?.nome,
    typeof linha?.coordenadorContrato === "object" && linha.coordenadorContrato
      ? extrairTexto(linha.coordenadorContrato) : null,
    typeof revisao?.coordenadorContrato === "object" && revisao.coordenadorContrato
      ? extrairTexto(revisao.coordenadorContrato) : null,
    // Simples
    linha?.coordenadorContratoNome,
    revisao?.coordenadorContratoNome,
    linha?.coordenadorNome,
    revisao?.coordenadorNome,
    linha?.collaboradorNome,
    revisao?.collaboradorNome,
    linha?.collaboratorName,
    revisao?.collaboratorName,
    // Inglês
    linha?.contractCoordinatorName,
    revisao?.contractCoordinatorName,
    linha?.projectCoordinatorName,
    revisao?.projectCoordinatorName
  ];

  for (const c of textoCandidatos) {
    const t = normalizarTexto(c || "");
    if (t && t !== "-" && t.toLowerCase() !== "null" && t.toLowerCase() !== "undefined") return t;
  }

  // 2. Lookup no mapa de Collaborator por ID
  const ids = extrairIdsCoordenador(linha, revisao);
  for (const id of ids) {
    const colab = mapa[id];
    if (!colab) continue;
    const nome = extrairTextoUtil(
      colab?.name, colab?.nome, colab?.fullName, colab?.displayName,
      colab?.userName, colab?.username
    );
    if (nome) return nome;
  }

  // 3. Fallback: se houver array de coordenadores, pega o primeiro
  const arraysCoordenadores = [
    Array.isArray(linha?.coordenadoresDeContratos) ? linha.coordenadoresDeContratos : null,
    Array.isArray(revisao?.coordenadoresDeContratos) ? revisao.coordenadoresDeContratos : null,
    Array.isArray(linha?.coordenadores) ? linha.coordenadores : null,
    Array.isArray(revisao?.coordenadores) ? revisao.coordenadores : null
  ];

  for (const arr of arraysCoordenadores) {
    if (!arr || !arr.length) continue;
    const primeiroCoord = arr[0];
    const nome = extrairTextoUtil(
      primeiroCoord?.nome,
      primeiroCoord?.name,
      primeiroCoord?.fullName,
      primeiroCoord?.displayName
    );
    if (nome) return nome;
  }

  return "-";
};

const normalizarItensJob = (linha = {}, revisao = {}) => {
  const candidatos = [
    revisao?.jobItens,
    linha?.jobItens,
    revisao?.itens,
    linha?.itens
  ];

  const lista = candidatos.find((valor) => Array.isArray(valor)) || [];

  return lista.map((item, index) => ({
    _id: item?._id || item?.id || `${linha?.code || "job"}-item-${index}`,
    codigo: item?.codigo || item?.sequencia || item?.itemCodigo || "-",
    descricao: normalizarTexto(item?.descricao || item?.description || item?.itemDescricao || "-"),
    quantidade: toNumber(item?.quantidade ?? item?.qtd ?? item?.quantity ?? 0),
    valorTotal: toNumber(item?.precoTotal ?? item?.valorTotal ?? item?.total ?? 0)
  }));
};

const extrairData = (...candidatos) => {
  for (const c of candidatos) {
    if (c && typeof c === "string" && c.length >= 8) return c;
  }
  return null;
};

const extrairBool = (...candidatos) => {
  for (const c of candidatos) {
    if (c === true) return true;
    if (c === false) return false;
    if (c === "S" || c === "s") return true;
    if (c === "N" || c === "n") return false;
  }
  return false;
};

const normalizarItensJobDetalhado = (linha = {}, revisao = {}) => {
  const candidatos = [
    revisao?.jobItens,
    linha?.jobItens,
    revisao?.itens,
    linha?.itens
  ];
  const lista = candidatos.find((valor) => Array.isArray(valor)) || [];
  return lista.map((item, index) => ({
    _id: item?._id || item?.id || `${linha?.code || "job"}-item-${index}`,
    codigo: item?.codigo || item?.sequencia || item?.itemCodigo || "-",
    descricao: normalizarTexto(item?.descricao || item?.description || item?.itemDescricao || "-"),
    tipo: normalizarTexto(item?.tipo || item?.type || item?.tipoOS || item?.tipoEquipamento || ""),
    quantidade: toNumber(item?.quantidade ?? item?.qtd ?? item?.quantity ?? 0),
    valorTotal: toNumber(item?.precoTotal ?? item?.valorTotal ?? item?.total ?? 0),
    dataFabricacaoPrevista: extrairData(item?.dataFabricacaoPrevista, item?.fabricacaoPrevista),
    dataFabricacaoEfetiva: extrairData(item?.dataFabricacaoEfetiva, item?.fabricacaoEfetiva),
    dataMontagemPrevista: extrairData(item?.dataMontagemPrevista, item?.montagemPrevista, item?.dateContratualMontagem),
    dataMontagemEfetiva: extrairData(item?.dataMontagemEfetiva, item?.montagemEfetiva, item?.dateEfetivaMontagem)
  }));
};

const normalizarLinha = (linha = {}, mapa = {}) => {
  const revisao = Array.isArray(linha?.revisoes) ? (linha.revisoes[0] || {}) : (linha?.revisoes || {});
  const itensJob = normalizarItensJobDetalhado(linha, revisao);

  const d = (f1, f2, f3) => extrairData(
    revisao?.[f1], linha?.[f1],
    revisao?.[f2], linha?.[f2],
    revisao?.[f3], linha?.[f3]
  );

  const coordenadorNome = resolverNomeCoordenador(linha, revisao, mapa);

  return {
    _id: linha?._id || linha?.id || `${linha?.code || "job"}-${Math.random()}`,
    _rawLinha: linha,
    code: linha?.code || "-",
    pedidoCompra: revisao?.pedidoCompra || linha?.pedidoCompra || "-",
    dataAbertura: d("dataAbertura"),
    dataEncerramento: d("dataEncerramento"),
    clienteNome: linha?.clienteNome || revisao?.clienteNome || "-",
    description: revisao?.description || linha?.description || "-",
    coordenadorNome,
    gestorContratosId: linha?.gestorDeContratos || revisao?.gestorDeContratos || null,
    gestorContratosNome: extrairTexto(revisao?.gestorDeContratosNome || linha?.gestorDeContratosNome || ""),
    contratosProjetosSigla: extrairSiglaContratosProjetos(linha, revisao),
    tipo: extrairTexto(revisao?.tipo || linha?.tipo || revisao?.tipoJob || linha?.tipoJob || ""),
    condicaoDePagamento: extrairTexto(revisao?.condicaoDePagamento || linha?.condicaoDePagamento || ""),
    multa: extrairBool(revisao?.multa, linha?.multa, revisao?.temMulta, linha?.temMulta),
    transporte: extrairBool(revisao?.transporte, linha?.transporte, revisao?.temTransporte, linha?.temTransporte),
    // Envio p/ Aprovação
    naoTemEnvioAprovacao: !!(revisao?.naoTemEnvioAprov || linha?.naoTemEnvioAprov),
    dataEnvioAprovacaoPrevista: d("dateEnvAprov", "dataEnvioAprovacaoContratual"),
    dataEnvioAprovacaoEfetiva: d("dateEnvEfetiva", "dataEnvioAprovacaoEfetiva"),
    // Aprovação
    naoTemAprovacao: !!(revisao?.naoTemAprov || linha?.naoTemAprov),
    dataAprovacaoPrevista: d("dateAprovPrevista", "dataAprovacaoPrevista"),
    dataAprovacaoEfetiva: d("dateAprovEfetiva", "dataAprovacaoEfetiva"),
    // Detalhamento (Inspeção)
    naoTemDetalhamento: !!(revisao?.naoTemInspec || linha?.naoTemInspec),
    dataDetalhamentoPrevista: d("dateInspecPrev", "dataInspecaoPrevista"),
    dataDetalhamentoEfetiva: d("dateInspecEfetiva", "dataInspecaoEfetiva"),
    // Fabricação (Entrega Fiscal no JOB)
    naoTemFabricacao: !!(revisao?.naoTemEntregaFis || linha?.naoTemEntregaFis),
    dataFabricacaoPrevista: d("dateEntregaFisPrev", "dataEntregaFiscalPrevista"),
    dataFabricacaoEfetiva: d("dateEntregaFisEfetiva", "dataEntregaFiscalEfetiva"),
    // Montagem
    naoTemMontagem: !!(revisao?.naoTemMontagem || linha?.naoTemMontagem),
    dataMontagemPrevista: d("dateContratualMontagem", "dataMontagemContratual"),
    dataMontagemEfetiva: d("dateEfetivaMontagem", "dataMontagemEfetiva"),
    // Entrega Final Contratual
    dataEntregaFinalPrevista: d("dateFinalContratual", "dataFinalContratual", "dataEntregaContratual"),
    dataEntregaFinalEfetiva: d("dateFinalEfetiva", "dataFinalEfetiva"),
    valorJob: toNumber(
      linha?.valorComIPIComICMS
      ?? revisao?.valorComIPIComICMS
      ?? linha?.valorVendaComImpostos
      ?? revisao?.valorVendaComImpostos
      ?? linha?.valorComImpostos
      ?? revisao?.valorComImpostos
      ?? linha?.valorJob
      ?? revisao?.valorJob
      ?? 0
    ),
    valorRecebido: 0,
    saldoAReceber: 0,
    valorFaturado: 0,
    saldoEmCarteira: 0,
    segmento: linha?.segmentoMercado || linha?.segmento || revisao?.segmentoMercado || revisao?.segmento || "",
    itensJob,
    itensQuantidade: itensJob.length
  };
};

// Busca em paralelo todos os IDs de Collaborator fornecidos e retorna mapa id→colaborador.
const buscarMapaColaboradores = async (ids = []) => {
  if (!ids.length) return {};
  
  try {
    const resultados = await Promise.allSettled(ids.map((id) => collaboratorService.getById(id)));
    
    const mapa = resultados.reduce((acc, resultado) => {
      if (resultado.status !== "fulfilled") return acc;
      
      const colab = resultado.value?.data?.data?._currentData || resultado.value?.data?.data || null;
      const id = String(colab?.id || colab?._id || "");
      
      if (!id) return acc;
      
      acc[id] = colab;
      return acc;
    }, {});

    return mapa;
  } catch (_) {
    return {};
  }
};

const listarPorView = async (params = []) => {
  try {
    const response = await viewService.viewRunById(JOB_VIEW_ID, 1, params);
    if (possuiDadosValidos(response)) {
      return extrairLista(response);
    }
  } catch (_) {
    // fallback para entidade
  }

  return [];
};

const listarPorEntidade = async () => {
  const response = await projectService.getAll(1, 5000, "", "", null, null, null, "code");
  return extrairLista(response);
};

const listarContasAReceberDaTela = async (filtros = {}) => {
  const params = [
    { dataType: "String", name: "dataInicio", value: filtros?.dataInicio || "1970-01-01" },
    {
      dataType: "String",
      name: "dataFim",
      value: formatarDataFimInclusiva(filtros?.dataFim) || formatarDataFimInclusiva(new Date().toISOString().slice(0, 10))
    }
  ];

  try {
    const response = await viewService.viewRun(CONTAS_A_RECEBER_VIEW_NAME, 1, params);
    if (possuiDadosValidos(response)) {
      return extrairLista(response);
    }
  } catch (_) {
    // fallback no serviço de relatório
  }

  return [];
};

const normalizarDataInicio = (valor = "") => {
  return toDateKey(valor);
};

const normalizarDataFim = (valor = "") => {
  return toDateKey(valor);
};

const filtrarLista = (lista = [], filtros = {}) => {
  const inicio = normalizarDataInicio(filtros?.dataInicio);
  const fim = normalizarDataFim(filtros?.dataFim);
  const jobFiltro = normalizarTexto(filtros?.job).toLowerCase();
  const clienteFiltro = normalizarTexto(filtros?.cliente).toLowerCase();
  const coordVendFiltro = normalizarTexto(filtros?.coordVend).toLowerCase();

  return lista.filter((item) => {
    const dataLinha = toDateKey(item?.dataAbertura);
    const dataInicioOk = inicio == null || (dataLinha != null && dataLinha >= inicio);
    const dataFimOk = fim == null || (dataLinha != null && dataLinha <= fim);

    const codeOk = !jobFiltro || normalizarTexto(item?.code).toLowerCase().includes(jobFiltro);
    const clienteOk = !clienteFiltro || normalizarTexto(item?.clienteNome).toLowerCase().includes(clienteFiltro);
    const coordVendOk = !coordVendFiltro || normalizarTexto(item?.vendedorCoordenador).toLowerCase().includes(coordVendFiltro);

    return dataInicioOk && dataFimOk && codeOk && clienteOk && coordVendOk;
  });
};

const somarRecebimentosPorJob = (linhas = []) => {
  return (Array.isArray(linhas) ? linhas : []).reduce((acc, linha) => {
    const chave = normalizarCodigoJob(
      linha?.job?.codigo ||
      linha?.jobCodigo ||
      linha?.jobCode ||
      linha?.codigoJob ||
      linha?.job ||
      linha?.project?.code ||
      linha?.project
    );

    if (!chave) return acc;

    const valorRecebido = toNumber(
      linha?.valorRecebido ??
      linha?.recebido ??
      linha?.valorLiquidado ??
      linha?.valorRecebimentos ??
      (toNumber(linha?.valorLiquido) - toNumber(linha?.saldo))
    );

    acc[chave] = toNumber(acc[chave]) + Math.max(0, valorRecebido);
    return acc;
  }, {});
};

const somarFaturamentoPorJob = (linhas = []) => {
  return (Array.isArray(linhas) ? linhas : []).reduce((acc, linha) => {
    if (!ehStatusEmitida(linha?.status)) return acc;
    if (!ehTipoNotaPermitidoNoFaturado(linha?.tipoNota)) return acc;

    const chave = normalizarCodigoJob(linha?.job || linha?.codigoJob || linha?.jobCode);
    if (!chave) return acc;

    acc[chave] = toNumber(acc[chave]) + toNumber(linha?.valorTotal);
    return acc;
  }, {});
};

export default {
  async listarRelatorioJob(filtros = {}) {
    const filtrosComDatasAuxiliares = {
      ...filtros,
      dataInicio: filtros?.dataInicio || "1970-01-01",
      dataFim: filtros?.dataFim || new Date().toISOString().slice(0, 10)
    };

    const params = [
      { dataType: "String", name: "dataInicio", value: filtrosComDatasAuxiliares.dataInicio },
      { dataType: "String", name: "dataFim", value: formatarDataFimInclusiva(filtrosComDatasAuxiliares.dataFim) },
      { dataType: "String", name: "codigo", value: filtros?.job || "" },
      { dataType: "String", name: "cliente", value: normalizarTexto(filtros?.cliente).toUpperCase() },
      { dataType: "String", name: "coordVend", value: normalizarTexto(filtros?.coordVend).toUpperCase() }
    ];

    // A View do backend já apresentou divergência no filtro de data do relatório.
    // Usa Project como fonte principal para que o filtro local por dataAbertura seja confiável.
    let lista = await listarPorEntidade();

    if (!Array.isArray(lista) || !lista.length) {
      lista = await listarPorView(params);
    }

    const listaRaw = Array.isArray(lista) ? lista : [];

    // 1. Coletar todos os IDs de coordenador dos registros brutos (antes de normalizar)
    const todasRevisoes = listaRaw.map((item) =>
      Array.isArray(item?.revisoes) ? (item.revisoes[0] || {}) : (item?.revisoes || {})
    );
    const todosIdsCoordenador = [...new Set(
      listaRaw.flatMap((item, i) => extrairIdsCoordenador(item, todasRevisoes[i]))
    )];

    // 2. Buscar todos os Collaborators em lote único
    const mapaCoordenador = await buscarMapaColaboradores(todosIdsCoordenador);

    // 3. Normalizar passando o mapa para resolução imediata do nome
    const normalizada = listaRaw.map((item) => normalizarLinha(item, mapaCoordenador));
    const filtrada = filtrarLista(normalizada, filtros);

    const [contasAReceberTela, contasAReceberRelatorio, nfEmitida] = await Promise.all([
      listarContasAReceberDaTela(filtrosComDatasAuxiliares),
      relatorioContasAReceberService.listarContasAReceber(filtrosComDatasAuxiliares),
      relatorioNFEmitidaService.listarNFEmitida(filtrosComDatasAuxiliares)
    ]);

    const baseContasAReceber = Array.isArray(contasAReceberTela) && contasAReceberTela.length
      ? contasAReceberTela
      : contasAReceberRelatorio;

    const recebimentosPorJob = somarRecebimentosPorJob(baseContasAReceber);
    const faturadoPorJob = somarFaturamentoPorJob(nfEmitida);

    return filtrada.map((item) => {
      const chaveJob = normalizarCodigoJob(item?.code);
      const valorRecebido = toNumber(recebimentosPorJob[chaveJob] || 0);
      const valorFaturado = toNumber(faturadoPorJob[chaveJob] || 0);
      const saldoAReceber = valorFaturado - valorRecebido;
      const saldoEmCarteira = toNumber(item.valorJob) - valorFaturado;

      return {
        ...item,
        valorRecebido,
        saldoAReceber,
        valorFaturado,
        saldoEmCarteira
      };
    });
  }
};
